import React, { useState, useEffect } from 'react';
import './GiveawayManagement.css';
import { giveawaysAPI } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';
import { hasPermission, PERMISSIONS } from '../utils/permissions';
import ConfirmDialog from './ConfirmDialog';
import CountdownTimer from './CountdownTimer';

function GiveawayManagement({ currentUser }) {
  const [giveaways, setGiveaways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedGiveaway, setSelectedGiveaway] = useState(null);
  const [showConfirm, setShowConfirm] = useState({ show: false, action: null, giveaway: null });

  useEffect(() => {
    loadGiveaways();
  }, []);

  const loadGiveaways = async () => {
    setLoading(true);
    try {
      const data = await giveawaysAPI.getAll();
      setGiveaways(data);
    } catch (error) {
      console.error('Failed to load giveaways:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (giveaway) => {
    if (!hasPermission(currentUser, PERMISSIONS.MANAGE_GIVEAWAYS)) {
      alert('You do not have permission to delete giveaways');
      return;
    }
    setShowConfirm({ show: true, action: 'delete', giveaway });
  };

  const handleEnd = async (giveaway) => {
    if (!hasPermission(currentUser, PERMISSIONS.MANAGE_GIVEAWAYS)) {
      alert('You do not have permission to end giveaways');
      return;
    }
    try {
      await giveawaysAPI.update(giveaway._id || giveaway.id, { status: 'ended' });
      loadGiveaways();
    } catch (error) {
      console.error('Failed to end giveaway:', error);
    }
  };

  const confirmAction = async () => {
    const { action, giveaway } = showConfirm;
    try {
      if (action === 'delete') {
        await giveawaysAPI.delete(giveaway._id || giveaway.id);
        alert('Giveaway deleted successfully');
      }
      loadGiveaways();
    } catch (error) {
      console.error('Failed to perform action:', error);
      alert('Failed to perform action');
    } finally {
      setShowConfirm({ show: false, action: null, giveaway: null });
    }
  };

  const filteredGiveaways = giveaways.filter(giveaway => {
    const matchesSearch = !searchQuery ||
      giveaway.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      giveaway.prize.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' ||
      (filter === 'active' && giveaway.status === 'active') ||
      (filter === 'ended' && giveaway.status === 'ended') ||
      (filter === 'cancelled' && giveaway.status === 'cancelled');
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return <LoadingSpinner message="Loading giveaways..." />;
  }

  return (
    <div className="giveaway-management-page">
      <div className="giveaway-management-container">
        <div className="giveaway-management-header">
          <h1>🎁 Giveaway Management</h1>
          <p>Manage all platform giveaways</p>
        </div>

        <div className="giveaway-management-controls">
          <input
            type="text"
            placeholder="Search giveaways..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="giveaway-search"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="giveaway-filter"
          >
            <option value="all">All Giveaways</option>
            <option value="active">Active</option>
            <option value="ended">Ended</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="giveaway-management-stats">
          <div className="stat-mini-card">
            <span className="stat-label">Total</span>
            <span className="stat-value">{giveaways.length}</span>
          </div>
          <div className="stat-mini-card">
            <span className="stat-label">Active</span>
            <span className="stat-value">{giveaways.filter(g => g.status === 'active').length}</span>
          </div>
          <div className="stat-mini-card">
            <span className="stat-label">Ended</span>
            <span className="stat-value">{giveaways.filter(g => g.status === 'ended').length}</span>
          </div>
          <div className="stat-mini-card">
            <span className="stat-label">Total Participants</span>
            <span className="stat-value">{giveaways.reduce((sum, g) => sum + (g.participants || 0), 0)}</span>
          </div>
        </div>

        <div className="giveaway-grid">
          {filteredGiveaways.map(giveaway => (
            <div key={giveaway._id || giveaway.id} className="giveaway-card-admin">
              <div className="giveaway-card-header">
                <h3>{giveaway.title}</h3>
                <span className={`status-badge status-${giveaway.status}`}>
                  {giveaway.status}
                </span>
              </div>
              <div className="giveaway-card-body">
                <p><strong>Prize:</strong> {giveaway.prize}</p>
                <p><strong>Participants:</strong> {giveaway.participants || 0}</p>
                {giveaway.status === 'active' && (
                  <div className="countdown-wrapper">
                    <strong>Ends in:</strong>
                    <CountdownTimer targetDate={giveaway.endsAt} />
                  </div>
                )}
                {giveaway.endsAt && (
                  <p><strong>End Date:</strong> {new Date(giveaway.endsAt).toLocaleDateString()}</p>
                )}
              </div>
              <div className="giveaway-card-actions">
                {hasPermission(currentUser, PERMISSIONS.MANAGE_GIVEAWAYS) && (
                  <>
                    {giveaway.status === 'active' && (
                      <button
                        className="action-btn end-btn"
                        onClick={() => handleEnd(giveaway)}
                      >
                        End Giveaway
                      </button>
                    )}
                    <button
                      className="action-btn delete-btn"
                      onClick={() => handleDelete(giveaway)}
                    >
                      Delete
                    </button>
                  </>
                )}
                <button
                  className="action-btn view-btn"
                  onClick={() => setSelectedGiveaway(giveaway)}
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredGiveaways.length === 0 && (
          <div className="empty-state">
            <p>No giveaways found</p>
          </div>
        )}

        {selectedGiveaway && (
          <div className="giveaway-details-modal" onClick={() => setSelectedGiveaway(null)}>
            <div className="giveaway-details-content" onClick={(e) => e.stopPropagation()}>
              <button className="close-btn" onClick={() => setSelectedGiveaway(null)}>×</button>
              <h2>Giveaway Details</h2>
              <div className="giveaway-details-grid">
                <div><strong>Title:</strong> {selectedGiveaway.title}</div>
                <div><strong>Prize:</strong> {selectedGiveaway.prize}</div>
                <div><strong>Status:</strong> {selectedGiveaway.status}</div>
                <div><strong>Participants:</strong> {selectedGiveaway.participants || 0}</div>
                <div><strong>Created:</strong> {new Date(selectedGiveaway.createdAt).toLocaleDateString()}</div>
                <div><strong>Ends:</strong> {new Date(selectedGiveaway.endsAt).toLocaleDateString()}</div>
                {selectedGiveaway.description && (
                  <div className="full-width"><strong>Description:</strong> {selectedGiveaway.description}</div>
                )}
              </div>
            </div>
          </div>
        )}

        <ConfirmDialog
          isOpen={showConfirm.show}
          title="Delete Giveaway"
          message={`Are you sure you want to delete "${showConfirm.giveaway?.title}"? This cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          onConfirm={confirmAction}
          onCancel={() => setShowConfirm({ show: false, action: null, giveaway: null })}
        />
      </div>
    </div>
  );
}

export default GiveawayManagement;

